/**
 * Tutorial Constants
 *
 * Defines tutorial tracks, step definitions, package mappings, and versioning.
 * This is the source of truth for tutorial content.
 */

// =============================================================================
// TUTORIAL TRACKS
// =============================================================================

export const TUTORIAL_TRACKS = {
  ORG_ADMIN: 'org_admin',
  EA: 'ea',
  SPEAKER: 'speaker',
  // Speaker bureau paradigm: day-to-day walkthrough for meeting planners.
  // Speaker bureau admins are onboarded by the standalone Getting Started
  // guide (/getting-started) rather than a walkthrough track.
  MEETING_PLANNER: 'meeting_planner',
};

// Human-readable names for each track, used by track pickers (e.g. the
// sidebar Tutorial submenu superadmins use to review any track)
export const TUTORIAL_TRACK_LABELS = {
  [TUTORIAL_TRACKS.ORG_ADMIN]: 'Admin Setup Guide',
  [TUTORIAL_TRACKS.EA]: 'EA Getting Started',
  [TUTORIAL_TRACKS.SPEAKER]: 'Speaker Portal Tour',
  [TUTORIAL_TRACKS.MEETING_PLANNER]: 'Meeting Planner Tour',
};

// Marker recorded in tutorial_state.completed_tracks after the standalone
// speaker bureau Getting Started guide has been auto-opened once for an
// org admin (so it only auto-opens on first login)
export const SPEAKER_BUREAU_GUIDE_MARKER = 'speaker_bureau_guide';

// =============================================================================
// STEP TYPES
// =============================================================================

export const STEP_TYPES = {
  MODAL: 'modal',
  SPOTLIGHT: 'spotlight',
  TOOLTIP: 'tooltip',
  HIGHLIGHT: 'highlight',
  GUIDED_ACTION: 'guided_action',
};

// =============================================================================
// TUTORIAL VERSIONS
// =============================================================================

export const TUTORIAL_VERSIONS = {
  [TUTORIAL_TRACKS.ORG_ADMIN]: '1.0.0',
  [TUTORIAL_TRACKS.EA]: '1.0.0',
  [TUTORIAL_TRACKS.SPEAKER]: '1.0.0',
  [TUTORIAL_TRACKS.MEETING_PLANNER]: '1.0.0',
};

// =============================================================================
// STEP LIFECYCLE STATES
// =============================================================================

export const STEP_LIFECYCLE = {
  ACTIVE: 'active',
  DEPRECATED: 'deprecated',
  REMOVED: 'removed',
  PREVIEW: 'preview',
};

// =============================================================================
// PACKAGE FEATURE REQUIREMENTS
// Maps step IDs to required package features
// =============================================================================

export const TUTORIAL_PACKAGE_REQUIREMENTS = {
  // Admin Tutorial Steps
  'admin-rbac': {
    packageFeatures: ['rbac_enabled'],
    description: 'RBAC configuration step',
  },
  'admin-teams': {
    packageFeatures: ['teams_enabled'],
    description: 'Team management step',
  },
  'admin-sso': {
    packageFeatures: ['sso_enabled'],
    description: 'SSO configuration step',
  },
  'admin-audit-logs': {
    packageFeatures: ['audit_logs_enabled'],
    description: 'Audit logs step',
  },

  // EA Tutorial Steps
  'ea-todos': {
    packageFeatures: ['todos_enabled'],
    description: 'Todo management step',
  },
  'ea-flight-verification': {
    packageFeatures: ['flight_verification_enabled'],
    description: 'Flight verification step',
  },
  'ea-urbanride': {
    packageFeatures: ['urbanride_quickbook_enabled'],
    description: 'Urbanride quick book step',
  },

  // Meeting Planner Tutorial Steps
  'planner-todos': {
    packageFeatures: ['todos_enabled'],
    description: 'Todo management step',
  },
};

// =============================================================================
// COMPONENT REQUIREMENTS
// Maps step IDs to required trip component types
// =============================================================================

export const TUTORIAL_COMPONENT_REQUIREMENTS = {
  'ea-add-flight': {
    componentType: 'flight',
    description: 'Adding flights step',
  },
  'ea-add-hotel': {
    componentType: 'hotel',
    description: 'Adding hotels step',
  },
  'ea-add-car': {
    componentType: 'car_service',
    description: 'Adding car service step',
  },
  'ea-add-rail': {
    componentType: 'rail',
    description: 'Adding rail step',
  },
  'ea-add-private-flight': {
    componentType: 'private_flight',
    description: 'Adding private flights step',
  },
};

// =============================================================================
// ORG ADMIN TUTORIAL STEPS
// =============================================================================

export const ORG_ADMIN_STEPS = [
  // Step 1: Welcome Modal
  {
    id: 'admin-welcome',
    type: STEP_TYPES.MODAL,
    title: 'Welcome, Administrator!',
    description: 'As an organization administrator, you have the power to set up and manage your team\'s travel management experience. Let\'s get you started.',
    content: {
      features: [
        { icon: 'Users', text: 'Invite and manage team members' },
        { icon: 'Building2', text: 'Organize teams and assign supervisors' },
        { icon: 'Shield', text: 'Configure permissions and access control' },
        { icon: 'CreditCard', text: 'Monitor billing and seat allocation' },
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Get Started', action: 'next' },
      secondary: { label: 'Skip Tutorial', action: 'dismiss' },
    },
  },

  // Step 2: Admin Dashboard Overview
  {
    id: 'admin-dashboard',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Admin Dashboard',
    description: 'This is your command center. Access user management, team configuration, and organization settings from here.',
    targetDataAttribute: 'admin-nav-link',
    route: '/admin',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 3: Users & Invites
  {
    id: 'admin-users-tab',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Users & Invites',
    description: 'Manage your team members here. View active users, send invitations, and manage account status.',
    targetDataAttribute: 'admin-users-link',
    route: '/admin',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.1.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 4: Access Management
  {
    id: 'admin-access-management',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Teams & Permissions',
    description: 'Organize users into teams, assign supervisors, and control who can access what across your organization.',
    targetDataAttribute: 'admin-access-link',
    route: '/access-management',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.1.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 5: Completion
  {
    id: 'admin-complete',
    type: STEP_TYPES.MODAL,
    title: 'You\'re All Set!',
    description: 'You now know the essentials of administering your organization.',
    content: {
      bullets: [
        'Invite your team members to get started',
        'Create teams to organize access',
        'Set up any custom permissions as needed',
        'Monitor billing and add seats as your team grows',
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Finish', action: 'complete' },
      secondary: { label: 'Restart Tutorial', action: 'restart' },
    },
  },
];

// =============================================================================
// EA TUTORIAL STEPS
// =============================================================================

export const EA_STEPS = [
  // Step 1: Welcome Modal
  {
    id: 'ea-welcome',
    type: STEP_TYPES.MODAL,
    title: 'Welcome to Veyage!',
    description: 'Veyage helps you manage travel for your executives efficiently. Let\'s take a quick tour of the key features you\'ll use every day.',
    content: {
      features: [
        { icon: 'User', text: 'Manage executive profiles and preferences' },
        { icon: 'Plane', text: 'Plan and track trips with all details in one place' },
        { icon: 'CheckSquare', text: 'Stay organized with task management' },
        { icon: 'Mail', text: 'Auto-import travel confirmations via email' },
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Start Tour', action: 'next' },
      secondary: { label: 'Skip for Now', action: 'dismiss' },
    },
  },

  // Step 2: Dashboard
  {
    id: 'ea-dashboard',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Your Home Base',
    description: 'The dashboard shows your upcoming trips, recent activity, and quick access to common actions. This is where you\'ll start each day.',
    targetDataAttribute: 'dashboard-main',
    route: '/',
    tooltipPosition: 'bottom',
    highlightPadding: 16,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 3: Executives
  {
    id: 'ea-executives',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Executive Profiles',
    description: 'Manage your executives here. Set up their travel preferences, loyalty programs, and contact details to streamline bookings.',
    targetDataAttribute: 'executives-nav-link',
    route: '/executives',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.1.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 4: Trips
  {
    id: 'ea-trips',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Trip Management',
    description: 'Create and manage trips with flights, hotels, car services, and more — all in one place.',
    targetDataAttribute: 'trips-nav-link',
    route: '/trips',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.1.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 5: Todos
  {
    id: 'ea-todos',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Task Management',
    description: 'Keep track of everything that needs to be done. Create todos, set deadlines, and never miss a detail.',
    targetDataAttribute: 'todos-nav-link',
    route: '/todos',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.1.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 6: Completion
  {
    id: 'ea-complete',
    type: STEP_TYPES.MODAL,
    title: 'You\'re Ready to Go!',
    description: 'You now know the essentials of Veyage. Here are some tips to get the most out of the platform:',
    content: {
      bullets: [
        'Set up your executive profiles with preferences first',
        'Use email forwarding to quickly import confirmations',
        'Check the dashboard daily for upcoming trips',
        'Use todos to track tasks and never miss a detail',
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Start Using Veyage', action: 'complete' },
      secondary: { label: 'Restart Tutorial', action: 'restart' },
    },
  },
];

// =============================================================================
// MEETING PLANNER TUTORIAL STEPS
// Day-to-day tour for meeting planners in speaker_bureau organizations.
// Replaces the EA track (whose copy is executive-travel specific) for
// these orgs.
// =============================================================================

export const MEETING_PLANNER_STEPS = [
  // Step 1: Welcome Modal
  {
    id: 'planner-welcome',
    type: STEP_TYPES.MODAL,
    title: 'Welcome, Meeting Planner!',
    description: 'Veyage keeps every speaker program — and all of its travel — in one place. Let\'s take a quick tour of the tools you\'ll use every day.',
    content: {
      features: [
        { icon: 'Mic', text: 'Work from your team\'s shared speaker roster' },
        { icon: 'Calendar', text: 'Create programs with flights, hotels, and transfers' },
        { icon: 'CheckSquare', text: 'Stay organized with task management' },
        { icon: 'Mail', text: 'Auto-import travel confirmations via email' },
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Start Tour', action: 'next' },
      secondary: { label: 'Skip for Now', action: 'dismiss' },
    },
  },

  // Step 2: Dashboard
  {
    id: 'planner-dashboard',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Your Home Base',
    description: 'The dashboard shows upcoming programs, recent activity, and quick access to common actions. This is where you\'ll start each day.',
    targetDataAttribute: 'dashboard-main',
    route: '/',
    tooltipPosition: 'bottom',
    highlightPadding: 16,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 3: Speakers
  {
    id: 'planner-speakers',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'The Speaker Roster',
    description: 'Your whole team shares this speaker list. Add speakers, keep their details current, and start new programs for them from here.',
    targetDataAttribute: 'executives-nav-link',
    route: '/executives',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 4: Trips
  {
    id: 'planner-trips',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Program Itineraries',
    description: 'Every speaker program has a trip where you manage flights, hotels, car service, and more — the complete itinerary in one place.',
    targetDataAttribute: 'trips-nav-link',
    route: '/trips',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 5: Email Settings — org parsing address + personal inbox allocation
  {
    id: 'planner-email-settings',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Your Inbox Addresses',
    description: 'Forward booking confirmations to your organization\'s parsing address and they\'re filed automatically. From this page you can also allocate your own personal addresses — a standard inbox shared with your team, and a confidential one only you (and admins) can see.',
    targetDataAttribute: 'email-settings-link',
    route: '/email-settings',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 6: Todos
  {
    id: 'planner-todos',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Task Management',
    description: 'Keep track of everything that needs to be done for each program. Create todos, set deadlines, and never miss a detail.',
    targetDataAttribute: 'todos-nav-link',
    route: '/todos',
    tooltipPosition: 'bottom',
    highlightPadding: 8,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Next', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },

  // Step 7: Completion
  {
    id: 'planner-complete',
    type: STEP_TYPES.MODAL,
    title: 'You\'re Ready to Go!',
    description: 'You now know the essentials. Here are some tips to get the most out of the platform:',
    content: {
      bullets: [
        'Keep speaker profiles up to date so programs start fast',
        'Use email forwarding to quickly import travel confirmations',
        'Check the dashboard daily for upcoming programs',
        'Use todos to track tasks and never miss a detail',
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Start Planning', action: 'complete' },
      secondary: { label: 'Restart Tutorial', action: 'restart' },
    },
  },
];

// =============================================================================
// SPEAKER TUTORIAL STEPS
// Speakers have a deliberately small, view-only surface (their engagements +
// trip itineraries), so the tour is two short steps.
// =============================================================================

export const SPEAKER_STEPS = [
  // Step 1: Welcome Modal
  {
    id: 'speaker-welcome',
    type: STEP_TYPES.MODAL,
    title: 'Welcome to Your Speaker Portal',
    description: 'Everything about your speaking engagements in one place — kept up to date by your meeting planners.',
    content: {
      features: [
        { icon: 'Briefcase', text: 'Your upcoming engagements at the top of the page' },
        { icon: 'Plane', text: 'Flights, hotels, and transfers in one travel timeline' },
        { icon: 'Calendar', text: 'Tap any trip to see its full itinerary' },
        { icon: 'Mail', text: 'Download a PDF of any itinerary to keep or share' },
      ],
    },
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Show Me', action: 'next' },
      secondary: { label: 'Skip', action: 'dismiss' },
    },
  },

  // Step 2: Dashboard + completion
  {
    id: 'speaker-dashboard',
    type: STEP_TYPES.SPOTLIGHT,
    title: 'Your Travel, At a Glance',
    description: 'Your engagements are listed at the top and every upcoming flight, hotel, and transfer appears below. Tap a trip or any card\'s trip link to open the full itinerary. Your planning team keeps all of this current for you.',
    targetDataAttribute: 'dashboard-main',
    route: '/',
    tooltipPosition: 'bottom',
    highlightPadding: 16,
    lifecycle: { state: STEP_LIFECYCLE.ACTIVE, addedInVersion: '1.0.0' },
    actions: {
      primary: { label: 'Done', action: 'next' },
      secondary: { label: 'Skip', action: 'skip' },
    },
  },
];

// =============================================================================
// TUTORIAL STEPS MAP
// =============================================================================

export const TUTORIAL_STEPS = {
  [TUTORIAL_TRACKS.ORG_ADMIN]: ORG_ADMIN_STEPS,
  [TUTORIAL_TRACKS.EA]: EA_STEPS,
  [TUTORIAL_TRACKS.SPEAKER]: SPEAKER_STEPS,
  [TUTORIAL_TRACKS.MEETING_PLANNER]: MEETING_PLANNER_STEPS,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all steps for a tutorial track
 * @param {string} track - Track name
 * @returns {Array} Array of step definitions
 */
export function getTutorialSteps(track) {
  return TUTORIAL_STEPS[track] || [];
}

/**
 * Get a specific step by ID within a track
 * @param {string} track - Track name
 * @param {string} stepId - Step ID
 * @returns {Object|null} Step definition or null
 */
export function getStepById(track, stepId) {
  const steps = getTutorialSteps(track);
  return steps.find(step => step.id === stepId) || null;
}

/**
 * Get the current version for a tutorial track
 * @param {string} track - Track name
 * @returns {string} Version string
 */
export function getTutorialVersion(track) {
  return TUTORIAL_VERSIONS[track] || '1.0.0';
}

/**
 * Check if a step requires specific package features
 * @param {string} stepId - Step ID
 * @returns {Object|null} Package requirements or null
 */
export function getStepPackageRequirements(stepId) {
  return TUTORIAL_PACKAGE_REQUIREMENTS[stepId] || null;
}

/**
 * Check if a step requires specific trip components
 * @param {string} stepId - Step ID
 * @returns {Object|null} Component requirements or null
 */
export function getStepComponentRequirements(stepId) {
  return TUTORIAL_COMPONENT_REQUIREMENTS[stepId] || null;
}

export default {
  TUTORIAL_TRACKS,
  TUTORIAL_TRACK_LABELS,
  STEP_TYPES,
  TUTORIAL_VERSIONS,
  STEP_LIFECYCLE,
  TUTORIAL_PACKAGE_REQUIREMENTS,
  TUTORIAL_COMPONENT_REQUIREMENTS,
  ORG_ADMIN_STEPS,
  EA_STEPS,
  SPEAKER_STEPS,
  MEETING_PLANNER_STEPS,
  TUTORIAL_STEPS,
  getTutorialSteps,
  getStepById,
  getTutorialVersion,
  getStepPackageRequirements,
  getStepComponentRequirements,
};
